import { memo, useCallback, useDeferredValue, useMemo, useState, type ReactElement } from "react";

import {
  Input,
  makeStyles,
  mergeClasses,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  tokens,
} from "@fluentui/react-components";
import { Search16Regular } from "@fluentui/react-icons";

import { HELP_PANEL_ATTR } from "./dom";
import type { HelpLanguage, HelpStep } from "./types";
import { groupStepsBySection, localizedStep, stepMatchesQuery } from "./types";
import { uiText, Z } from "./ui";

/**
 * "Jump to a step" — the table of contents behind the tour's step counter.
 *
 * A tour is a linear walk; someone who wants one field in the middle of it should not press Next
 * until it arrives, and a user who cannot name the field needs to search the prose, not scan
 * titles — hence `stepMatchesQuery`, which reads bodies too. Grouped by the guide's own sections,
 * because the sections are how the page is laid out.
 */

const useStyles = makeStyles({
  surface: {
    width: "340px",
    borderRadius: "16px",
    padding: 0,
  },
  searchWrap: { padding: "10px 10px 6px" },
  search: { width: "100%" },
  list: { maxHeight: "340px", overflowY: "auto", padding: "0 10px 10px" },
  empty: { fontSize: "12px", color: tokens.colorNeutralForegroundDisabled, padding: "12px 4px" },
  group: {
    fontSize: "9.5px",
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "1px",
    textTransform: "uppercase",
    color: tokens.colorNeutralForegroundDisabled,
    padding: "0 6px",
    marginTop: "8px",
    marginBottom: "2px",
  },
  row: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
    textAlign: "left",
    padding: "5px 6px",
    borderRadius: "6px",
    border: "none",
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorSubtleBackgroundHover },
  },
  rowCurrent: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  rowTitle: {
    fontSize: "12.5px",
    fontWeight: tokens.fontWeightMedium,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowTitleCurrent: { fontWeight: tokens.fontWeightBold },
  rowIndex: { fontSize: "11px", color: tokens.colorNeutralForegroundDisabled, flexShrink: 0 },
  footer: {
    padding: "6px 16px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: "11px",
    color: tokens.colorNeutralForegroundDisabled,
  },
});

/** One line of the table of contents, memoised: the list is rebuilt on every keystroke. */
const JumpRow = memo(
  ({
    step,
    language,
    index,
    current,
    onPick,
  }: {
    step: HelpStep;
    language: HelpLanguage;
    /** The step's place in the tour — the number the reader sees, and what `onPick` reports. */
    index: number;
    current: boolean;
    onPick: (index: number) => void;
  }) => {
    const styles = useStyles();
    return (
      <button
        type="button"
        onClick={() => onPick(index)}
        aria-current={current ? "step" : undefined}
        className={mergeClasses(styles.row, current && styles.rowCurrent)}
      >
        <span className={mergeClasses(styles.rowTitle, current && styles.rowTitleCurrent)}>
          {localizedStep(step, language).title}
        </span>
        <span className={styles.rowIndex}>{index + 1}</span>
      </button>
    );
  }
);
JumpRow.displayName = "JumpRow";

interface HelpStepJumpProps {
  open: boolean;
  /** The step counter — the element the popover hangs from and toggles it. */
  trigger: ReactElement;
  /** The tour's steps, in tour order — indices into this array are what `onPick` returns. */
  steps: HelpStep[];
  /** `PageHelp.sections`, for the grouping order. */
  sections?: string[];
  language: HelpLanguage;
  currentIndex: number;
  onOpenChange: (open: boolean) => void;
  onPick: (index: number) => void;
}

export const HelpStepJump = ({
  open,
  trigger,
  steps,
  sections,
  language,
  currentIndex,
  onOpenChange,
  onPick,
}: HelpStepJumpProps) => {
  const styles = useStyles();
  const text = uiText(language);
  const [query, setQuery] = useState("");
  /**
   * The list may lag a frame behind the typing; the field may not. Enter is the exception and is
   * resolved against the LIVE query — a jump to the previous keystroke's first match is wrong.
   */
  const deferredQuery = useDeferredValue(query);

  const matches = useMemo(
    () => steps.filter((step) => stepMatchesQuery(step, deferredQuery)),
    [steps, deferredQuery]
  );
  const groups = useMemo(() => groupStepsBySection(sections, matches), [sections, matches]);
  /** Tour position by selector, built once per tour rather than by `indexOf` per row. */
  const positionOf = useMemo(
    () => new Map(steps.map((step, index) => [step.selector, index] as const)),
    [steps]
  );

  const pick = useCallback(
    (index: number): void => {
      onPick(index);
      setQuery("");
      onOpenChange(false);
    },
    [onPick, onOpenChange]
  );

  return (
    <Popover
      open={open}
      onOpenChange={(_event, data) => onOpenChange(data.open)}
      positioning="below-start"
      trapFocus
    >
      <PopoverTrigger disableButtonEnhancement>{trigger}</PopoverTrigger>
      <PopoverSurface
        className={styles.surface}
        // Above the tour card (Z.card); a default popover would open underneath it.
        style={{ zIndex: Z.jump }}
        {...{ [HELP_PANEL_ATTR]: "jump" }}
      >
        <div className={styles.searchWrap}>
          <Input
            className={styles.search}
            size="small"
            autoFocus
            value={query}
            onChange={(_event, data) => setQuery(data.value)}
            placeholder={text.jumpPlaceholder}
            contentBefore={<Search16Regular />}
            maxLength={60}
            aria-label={text.jumpAria}
            data-help="help-jump-search"
            onKeyDown={(event) => {
              if (event.key !== "Enter") return;
              const best = steps.findIndex((step) => stepMatchesQuery(step, query));
              if (best < 0) return;
              event.preventDefault();
              pick(best);
            }}
          />
        </div>

        <div className={styles.list}>
          {matches.length === 0 && <div className={styles.empty}>{text.jumpEmpty}</div>}

          {groups.map((group) => (
            <div key={group.section}>
              <div className={styles.group}>{group.section}</div>
              {group.steps.map((step) => {
                const index = positionOf.get(step.selector);
                // Every grouped step came out of `steps`; render nothing rather than a row that
                // would jump to step 1 if that ever stopped holding.
                if (index === undefined) return null;
                return (
                  <JumpRow
                    key={step.selector}
                    step={step}
                    language={language}
                    index={index}
                    current={index === currentIndex}
                    onPick={pick}
                  />
                );
              })}
            </div>
          ))}
        </div>

        {deferredQuery.length > 0 && matches.length > 0 && (
          <div className={styles.footer}>{text.jumpFooter(matches.length, steps.length)}</div>
        )}
      </PopoverSurface>
    </Popover>
  );
};
